import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import {
  createAgentBroadcast,
  fetchAgentPlayers,
  fetchDashboard,
  fetchManagedAgents,
  fetchShops,
} from '../services/api';
import type { AgentDashboardData, AgentPlayer, AgentShop, SuperManagedAgent } from '../types';
import { PAGE } from '@kioskgaming/ui';

const TITLE_MAX = 500;
const BODY_MAX = 1000;

type TargetKind = 'agent' | 'shop' | 'player';

export default function SendBroadcastPage() {
  const [dash, setDash] = useState<AgentDashboardData | null>(null);
  const [agents, setAgents] = useState<SuperManagedAgent[]>([]);
  const [shops, setShops] = useState<AgentShop[]>([]);
  const [players, setPlayers] = useState<AgentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetKind, setTargetKind] = useState<TargetKind>('agent');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a, s, p] = await Promise.all([
        fetchDashboard(),
        fetchManagedAgents(),
        fetchShops(),
        fetchAgentPlayers(),
      ]);
      if (d.success && d.data) setDash(d.data);
      if (a.success && a.data) setAgents(a.data.filter((x) => x.status === 'active'));
      if (s.success && s.data) setShops(s.data.filter((x) => x.status === 'active'));
      if (p.success && p.data) setPlayers(p.data.filter((x) => x.status === 'active'));
    } catch {
      toast.error('Could not load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dash?.can_delegate_popups) return;
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (selected.size === 0) {
      const kindLabel = targetKind === 'agent' ? 'agent' : targetKind === 'shop' ? 'shop' : 'player';
      toast.error(`Select at least one ${kindLabel}`);
      return;
    }
    setSubmitting(true);
    try {
      const selectedIds = Array.from(selected);
      const res = await createAgentBroadcast({
        title: title.trim(),
        body: body.trim(),
        targetAgentIds: targetKind === 'agent' ? selectedIds : [],
        targetShopIds: targetKind === 'shop' ? selectedIds : [],
        targetUserIds: targetKind === 'player' ? selectedIds : [],
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      if (res.success) {
        toast.success('Broadcast sent');
        setTitle('');
        setBody('');
        setExpiresAt('');
        setSelected(new Set());
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageLoadingIndicator variant="page" />
      </AppLayout>
    );
  }

  if (dash && !dash.can_delegate_popups) {
    return <Navigate to="/" replace />;
  }

  const items: (SuperManagedAgent | AgentShop | AgentPlayer)[] =
    targetKind === 'agent' ? agents : targetKind === 'shop' ? shops : players;

  const audiencePlural =
    targetKind === 'agent' ? 'Agents' : targetKind === 'shop' ? 'Shops' : 'Players';

  return (
    <AppLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-2">{PAGE.broadcasts}</h1>
      <p className="text-sm text-gray-600 mb-6 max-w-xl">
        Send a bell notification to Agents, Shops or Players in your network. This uses the same permission as
        Popups.
      </p>
      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-2">
          <p className="text-sm font-medium text-gray-800">Audience</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-broadcast-target"
              checked={targetKind === 'agent'}
              onChange={() => {
                setTargetKind('agent');
                setSelected(new Set());
              }}
            />
            <span>Agents</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-broadcast-target"
              checked={targetKind === 'shop'}
              onChange={() => {
                setTargetKind('shop');
                setSelected(new Set());
              }}
            />
            <span>Shops</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="agent-broadcast-target"
              checked={targetKind === 'player'}
              onChange={() => {
                setTargetKind('player');
                setSelected(new Set());
              }}
            />
            <span>Players</span>
          </label>
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Title</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
            maxLength={TITLE_MAX}
            required
          />
        </div>
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-2">
            <label className="text-sm font-medium text-gray-700">Message</label>
            <span className="text-xs text-gray-500 tabular-nums">
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            className="w-full min-h-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            maxLength={BODY_MAX}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hide after (optional)</label>
          <input
            type="datetime-local"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{audiencePlural}</p>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">
              No active {targetKind === 'agent' ? 'agents' : targetKind === 'shop' ? 'shops' : 'players'}.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
              {items.map((it) => (
                <label
                  key={it.id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                >
                  <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
                  {targetKind === 'agent' ? (
                    <>
                      <span className="font-medium text-gray-900">{(it as SuperManagedAgent).name}</span>
                      <span className="text-gray-500 truncate">{(it as SuperManagedAgent).email}</span>
                    </>
                  ) : targetKind === 'shop' ? (
                    <>
                      <span className="font-medium text-gray-900">{(it as AgentShop).name}</span>
                      <span className="text-gray-500 truncate">{(it as AgentShop).email}</span>
                    </>
                  ) : (
                    <span className="font-medium text-gray-900 truncate">
                      {(it as AgentPlayer).phone || (it as AgentPlayer).email || (it as AgentPlayer).username || it.id}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting || items.length === 0}
          className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? 'Sending…' : 'Send broadcast'}
        </button>
      </form>
    </AppLayout>
  );
}
